import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, MapPin, TrendingUp, Award, Package, Users, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import PrivacyConsentNotice from "@/components/PrivacyConsentNotice";
import { trackFormLeadConversion } from "@/lib/adsTracking";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  {
    icon: MapPin,
    title: "Exclusive Territory",
    description: "Get exclusive distribution rights for your city or region with protected territory agreements.",
  },
  {
    icon: TrendingUp,
    title: "High Margins",
    description: "Competitive margins with volume-based incentives and quarterly performance bonuses.",
  },
  {
    icon: Package,
    title: "Wide Product Range",
    description: "Access to complete office furniture range - workstations, cabins, conference tables, seating, and more.",
  },
  {
    icon: Award,
    title: "Marketing Support",
    description: "Co-branded marketing materials, catalogs, and digital assets for local promotion.",
  },
  {
    icon: Users,
    title: "Training & Support",
    description: "Product training, sales support, and dedicated relationship manager for your territory.",
  },
  {
    icon: Shield,
    title: "Quality Assurance",
    description: "ISO 9001:2015 certified products with warranty support and after-sales service backing.",
  },
];

const requirements = [
  "Existing furniture or interior business preferred",
  "Strong local network in corporate/commercial segment",
  "Showroom space (minimum 500 sq.ft)",
  "Dedicated sales team for B2B sales",
  "Financial capability for inventory investment",
  "Commitment to brand standards and quality service",
];

export default function DistributorsPage() {
  useCanonical("https://ezeefitofficesystems.in/distributors");
  useMetaDescription("Become an Ezeefit distributor — partner with a Mumbai modular office furniture manufacturer. Workstations, chairs, partitions for Maharashtra & India.");
  useEffect(() => {
    document.title = "Become a Distributor | Modular Office Furniture Manufacturer Mumbai | Ezeefit";
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    current_business: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/enquiries`, {
        company_name: formData.company,
        contact_person: formData.name,
        email: formData.email,
        phone: formData.phone,
        industry: "Distributor Application",
        project_location: `${formData.city}, ${formData.state}`,
        seats_planned: "N/A",
        timeline: "Immediate",
        budget_range: "Distributor Partnership",
        design_support: formData.current_business,
        message: formData.message,
      });
      trackFormLeadConversion({ formName: "distributor_application" });
      setIsSubmitted(true);
      toast.success("Application submitted! Our team will contact you soon.");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="distributors-page">
      <Helmet>
        <meta name="description" content="Become an Ezeefit distributor — partner with a Mumbai modular office furniture manufacturer. Workstations, chairs, partitions for Maharashtra & India." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/distributors" />
      </Helmet>
      {/* Hero Banner */}
      <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1280&q=70"
            alt="Business partnership"
            className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block bg-[#F97316] text-white text-sm font-mono px-4 py-2 uppercase tracking-wider mb-6">
              Business Opportunity
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Partner with Ezeefit:<br/>
              <span className="text-[#F97316]">Distribute Modular Office Furniture</span>
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto mb-8">
              Join our distributor network. Unlock exclusive territories and competitive margins. 
              Bring modular furniture solutions to your market.
            </p>
            <Button
              asChild
              className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider text-lg"
            >
              <a href="#apply-form">
                Apply for Distributorship
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "2014", label: "Established" },
              { value: "ISO 9001:2015", label: "Certified Products" },
              { value: "Mumbai", label: "Own Manufacturing Facility" },
              { value: "Pan-India", label: "Project Execution" },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="font-mono text-3xl md:text-4xl font-bold text-[#F97316]">
                  {stat.value}
                </div>
                <div className="text-sm text-[#64748B] mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Why Partner With Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]">
              Distributor Benefits
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-white border border-[#E2E8F0] card-hover"
              >
                <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#F97316]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#64748B]">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Range */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Product Portfolio
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Complete Office Furniture Range
              </h2>
              <p className="text-lg text-[#64748B] mb-8">
                As an Ezeefit distributor, you'll have access to our complete range of 
                modular office furniture solutions:
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  "Linear Workstations",
                  "L-Shape Workstations",
                  "Executive Desks",
                  "Conference Tables",
                  "Phone Booths",
                  "Discussion Pods",
                  "Filing Cabinets",
                  "Office Chairs",
                  "Reception Counters",
                  "Partition Systems",
                ].map((product) => (
                  <div key={product} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-[#F97316]" />
                    <span className="text-[#334155]">{product}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70"
                alt="Modern office"
                className="w-full h-48 object-cover" loading="lazy" decoding="async" />
              <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1280&q=70"
                alt="Executive office"
                className="w-full h-48 object-cover" loading="lazy" decoding="async" />
              <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1280&q=70"
                alt="Coworking space"
                className="w-full h-48 object-cover" loading="lazy" decoding="async" />
              <img src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1280&q=70"
                alt="Call center"
                className="w-full h-48 object-cover" loading="lazy" decoding="async" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Partner Profile
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Who We're Looking For
              </h2>
              <p className="text-lg text-[#94A3B8] mb-8">
                We seek committed partners who share our vision for quality and customer service.
              </p>
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#CBD5E1]">{req}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-6">Ideal Partner</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-[#F97316] font-semibold mb-2">Existing Business</h4>
                  <p className="text-[#94A3B8] text-sm">Furniture dealers, interior contractors, or office fit-out companies</p>
                </div>
                <div>
                  <h4 className="text-[#F97316] font-semibold mb-2">Market Reach</h4>
                  <p className="text-[#94A3B8] text-sm">Strong connections with corporates, architects, and builders</p>
                </div>
                <div>
                  <h4 className="text-[#F97316] font-semibold mb-2">Infrastructure</h4>
                  <p className="text-[#94A3B8] text-sm">Showroom space and team for display and sales</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Apply Now
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              Start Your Partnership Journey
            </h2>
            <p className="text-lg text-[#64748B]">
              Fill the form below and our business development team will contact you within 48 hours.
            </p>
          </motion.div>

          {isSubmitted ? (
            <div className="text-center py-12 bg-[#F8FAFC] border border-[#E2E8F0]">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-3">Application Received!</h3>
              <p className="text-[#64748B] mb-6">
                Thank you for your interest. Our team will review your application and contact you within 48 hours.
              </p>
            </div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="bg-[#F8FAFC] border border-[#E2E8F0] p-8 md:p-12"
              data-testid="distributor-form"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">Full Name *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="h-12 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">Company Name *</label>
                  <Input
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Your company name"
                    className="h-12 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">Email *</label>
                  <Input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@company.com"
                    className="h-12 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">Phone *</label>
                  <Input
                    required
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="9820042100"
                    className="h-12 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">City *</label>
                  <Input
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Your city"
                    className="h-12 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">State *</label>
                  <Input
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="Your state"
                    className="h-12 rounded-none"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#334155] mb-2">Current Business *</label>
                <Select onValueChange={(value) => setFormData({ ...formData, current_business: value })}>
                  <SelectTrigger className="h-12 rounded-none">
                    <SelectValue placeholder="Select your current business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Furniture Dealer">Furniture Dealer</SelectItem>
                    <SelectItem value="Interior Contractor">Interior Contractor</SelectItem>
                    <SelectItem value="Office Fit-out Company">Office Fit-out Company</SelectItem>
                    <SelectItem value="Architect/Designer">Architect/Designer</SelectItem>
                    <SelectItem value="Real Estate/Builder">Real Estate/Builder</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-[#334155] mb-2">Tell us about your business</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Brief description of your current business, market reach, and why you want to partner with Ezeefit..."
                  className="min-h-[120px] rounded-none resize-none"
                />
              </div>

              <PrivacyConsentNotice className="mb-2" />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold text-base uppercase tracking-wider"
              >
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </Button>
            </motion.form>
          )}
        </div>
      </section>

      <ExploreMore currentPage="distributors" />
    </div>
  );
}
